import { Link, Meta, Title } from '@solidjs/meta';
import {
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
} from 'solid-js';
import { locale, t } from '@/shared/i18n';

const BASE_URL = 'https://khoai.io';
const DEFAULT_OG_IMAGE = '/og-welcome.jpg';
const LOCATION_CHANGE_EVENT = 'khoai:location-change';

type RouteMeta = {
  title: string;
  description: string;
  canonicalPath: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  publishedTime?: string;
  noindex?: boolean;
  includeAlternates?: boolean;
};

function withBrand(title: string): string {
  return `${title} - Khoai`;
}

function isViPath(pathname: string): boolean {
  return pathname === '/vi' || pathname.startsWith('/vi/');
}

function normalizePath(pathname: string): string {
  if (pathname === '/vi') return '/welcome';
  if (pathname.startsWith('/vi/')) return pathname.slice(3) || '/';
  return pathname;
}

function resolveAppRouteMeta(pathname: string): RouteMeta {
  const isVi = isViPath(pathname);
  const normalized = normalizePath(pathname);
  const localizedPath = isVi ? `/vi${normalized}` : normalized;

  const genericDescription = isVi
    ? 'Nen tang quan ly kho, nhap xuat va van hanh cua hang theo thoi gian thuc voi khoai.io.'
    : 'Real-time inventory, transfer, and store operations management with khoai.io.';

  const authDescription = isVi
    ? 'Dang nhap va quan ly tai khoan khoai.io mot cach an toan.'
    : 'Secure access and account flows for your khoai.io workspace.';

  switch (normalized) {
    case '/welcome':
      return {
        title: withBrand(t('seo.landing.title')),
        description: t('seo.landing.desc'),
        canonicalPath: localizedPath,
        ogImage: '/og-welcome.jpg',
        includeAlternates: true,
      };
    case '/pricing':
      return {
        title: withBrand(t('public.nav.pricing')),
        description: t('seo.pricing.desc'),
        canonicalPath: localizedPath,
        includeAlternates: true,
      };
    case '/features/inventory-management':
      return {
        title: withBrand(t('feature.inventory.tag')),
        description: t('feature.inventory.subtitle'),
        canonicalPath: localizedPath,
        includeAlternates: true,
      };
    case '/features/ai-analytics':
      return {
        title: withBrand(t('feature.analytics.tag')),
        description: t('feature.analytics.subtitle'),
        canonicalPath: localizedPath,
        ogImage:
          locale() === 'vi'
            ? '/inventory_risk_forecast_vn.jpg'
            : '/inventory_risk_forecast_en.jpg',
        includeAlternates: true,
      };
    case '/features/barcode-mobile':
      return {
        title: withBrand(t('feature.barcode.tag')),
        description: t('feature.barcode.subtitle'),
        canonicalPath: localizedPath,
        includeAlternates: true,
      };
    case '/features/team-permissions':
      return {
        title: withBrand(t('feature.team.tag')),
        description: t('feature.team.subtitle'),
        canonicalPath: localizedPath,
        includeAlternates: true,
      };
    case '/privacy':
      return {
        title: withBrand(t('privacy.title')),
        description: t('seo.privacy.desc'),
        canonicalPath: localizedPath,
        includeAlternates: true,
      };
    case '/terms':
      return {
        title: withBrand(t('terms.title')),
        description: t('seo.terms.desc'),
        canonicalPath: localizedPath,
        includeAlternates: true,
      };
    case '/billing-terms':
      return {
        title: withBrand(t('billingTerms.title')),
        description: t('seo.billingTerms.desc'),
        canonicalPath: localizedPath,
        includeAlternates: true,
      };
    case '/blogs':
      return {
        title: withBrand('Blog — Inventory & Operations Insights'),
        description:
          'Practical writing on inventory management, warehouse operations, and logistics automation from the team behind Khoai.',
        canonicalPath: localizedPath,
        ogImage: '/blog-spreadsheet-trap-hero.jpg',
        includeAlternates: true,
      };
    case '/blogs/spreadsheet-trap':
      return {
        title: withBrand(
          'The Spreadsheet Trap: What Your Inventory System Is Costing You'
        ),
        description:
          'Ghost stock, data fragmentation, and zero real-time visibility — the hidden costs of running warehouse operations on spreadsheets, and how modern platforms eliminate them.',
        canonicalPath: localizedPath,
        ogImage: '/blog-spreadsheet-trap-hero.jpg',
        ogType: 'article',
        publishedTime: '2026-05-06',
        includeAlternates: true,
      };
    case '/blogs/walking-tax':
      return {
        title: withBrand(
          'The Walking Tax: How Warehouse Layout Is Quietly Draining Your Labor Budget'
        ),
        description:
          'In an unoptimized warehouse, a single worker can walk 10+ miles per shift. Learn how velocity slotting and zone picking cut floor travel by 20% — the equivalent of a free staff member.',
        canonicalPath: localizedPath,
        ogImage: '/blog-walking-tax-tired-worker.jpg',
        ogType: 'article',
        publishedTime: '2026-05-06',
        includeAlternates: true,
      };
    case '/blogs/ghost-inventory':
      return {
        title: withBrand(
          'The Ghost in the Machine: Eliminating Phantom Stock and Dead Inventory'
        ),
        description:
          'Phantom stock and dead inventory are silent profit killers. Learn how cycle counting, lean reorder points, FIFO batch tracking, and real-time sync eliminate them for good.',
        canonicalPath: localizedPath,
        ogImage: '/blog-ghost-inventory.jpg',
        ogType: 'article',
        publishedTime: '2026-05-06',
        includeAlternates: true,
      };
    case '/blogs/growth-trap':
      return {
        title: withBrand(
          'The Growth Trap: Why Your Manual Systems Will Break at 50 Orders a Day'
        ),
        description:
          'What worked at 5 orders a day becomes a nightmare at 50. Learn about "technical debt" in the warehouse, why barcode scanning is the single biggest accuracy upgrade, and the 60-second stress test to know if you\'re ready to scale.',
        canonicalPath: localizedPath,
        ogImage: '/blog-scaling-chaos.jpg',
        ogType: 'article',
        publishedTime: '2026-05-08',
        includeAlternates: true,
      };
    case '/blogs/crystal-ball':
      return {
        title: withBrand(
          'The Warehouse Crystal Ball: Using AI to Predict Demand Before It Happens'
        ),
        description:
          'Tired of running out of bestsellers while sitting on slow-moving stock? Learn how AI-powered demand forecasting calculates sales velocity per SKU, surfaces confidence-scored predictions, and turns forecasting intelligence into one-click purchase orders.',
        canonicalPath: localizedPath,
        ogImage: '/blog-forecast-hero.jpg',
        ogType: 'article',
        publishedTime: '2026-05-09',
        includeAlternates: true,
      };
    case '/login':
      return {
        title: withBrand(t('auth.signIn')),
        description: authDescription,
        canonicalPath: '/login',
        noindex: true,
      };
    case '/signup':
      return {
        title: withBrand(t('auth.signUp')),
        description: authDescription,
        canonicalPath: '/signup',
        noindex: true,
      };
    case '/forgot-password':
      return {
        title: withBrand(t('auth.forgotPasswordTitle')),
        description: authDescription,
        canonicalPath: '/forgot-password',
        noindex: true,
      };
    case '/reset-password':
      return {
        title: withBrand(t('auth.resetPasswordTitle')),
        description: authDescription,
        canonicalPath: '/reset-password',
        noindex: true,
      };
    case '/onboarding':
      return {
        title: withBrand(t('auth.onboarding.completeRegistration')),
        description: authDescription,
        canonicalPath: '/onboarding',
        noindex: true,
      };
    case '/setup':
      return {
        title: withBrand(t('auth.setup.title')),
        description: authDescription,
        canonicalPath: '/setup',
        noindex: true,
      };
    case '/no-business':
      return {
        title: withBrand(t('auth.noBusiness.title')),
        description: authDescription,
        canonicalPath: '/no-business',
        noindex: true,
      };
    default:
      return {
        title: withBrand('khoai dashboard'),
        description: genericDescription,
        canonicalPath: pathname,
        noindex: normalized.startsWith('/dev'),
      };
  }
}

export function RouteSeo() {
  const [pathname, setPathname] = createSignal(
    typeof window !== 'undefined' ? window.location.pathname : '/'
  );

  onMount(() => {
    const patchKey = '__khoaiLocationPatchApplied';
    const historyWithPatch = history as History & {
      [patchKey]?: boolean;
      __khoaiOriginalPushState?: History['pushState'];
      __khoaiOriginalReplaceState?: History['replaceState'];
    };

    if (!historyWithPatch[patchKey]) {
      historyWithPatch[patchKey] = true;
      historyWithPatch.__khoaiOriginalPushState =
        history.pushState.bind(history);
      historyWithPatch.__khoaiOriginalReplaceState =
        history.replaceState.bind(history);

      history.pushState = (...args) => {
        historyWithPatch.__khoaiOriginalPushState?.(...args);
        window.dispatchEvent(new Event(LOCATION_CHANGE_EVENT));
      };

      history.replaceState = (...args) => {
        historyWithPatch.__khoaiOriginalReplaceState?.(...args);
        window.dispatchEvent(new Event(LOCATION_CHANGE_EVENT));
      };
    }

    const handleLocationChange = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', handleLocationChange);
    window.addEventListener(LOCATION_CHANGE_EVENT, handleLocationChange);

    onCleanup(() => {
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener(LOCATION_CHANGE_EVENT, handleLocationChange);
    });
  });

  const meta = createMemo(() => resolveAppRouteMeta(pathname()));
  const canonicalUrl = createMemo(() => `${BASE_URL}${meta().canonicalPath}`);
  const ogImageUrl = createMemo(() => {
    const imagePath = meta().ogImage ?? DEFAULT_OG_IMAGE;
    return imagePath.startsWith('/') ? `${BASE_URL}${imagePath}` : imagePath;
  });

  const enAltUrl = createMemo(() => {
    const canonicalPath = meta().canonicalPath;
    const normalized = normalizePath(canonicalPath);
    return `${BASE_URL}${normalized}`;
  });

  const viAltUrl = createMemo(() => {
    const canonicalPath = meta().canonicalPath;
    const normalized = normalizePath(canonicalPath);
    return `${BASE_URL}/vi${normalized}`;
  });

  // Prerender emits a canonical tag server-side and @solidjs/meta emits one
  // client-side. Keep exactly one canonical in the live DOM to avoid SEO
  // tooling reporting conflicting canonical URLs.
  onMount(() => {
    createEffect(() => {
      const url = canonicalUrl();
      if (typeof document === 'undefined') return;

      const existing = Array.from(
        document.head.querySelectorAll('link[rel="canonical"]')
      ) as HTMLLinkElement[];

      let keeper = document.head.querySelector(
        'link[data-khoai-canonical="true"]'
      ) as HTMLLinkElement | null;

      if (!keeper) {
        keeper = document.createElement('link');
        keeper.setAttribute('rel', 'canonical');
        keeper.setAttribute('data-khoai-canonical', 'true');
        document.head.appendChild(keeper);
      }

      keeper.href = url;

      for (const link of existing) {
        if (link !== keeper) {
          link.remove();
        }
      }
    });
  });

  return (
    <>
      <Title>{meta().title}</Title>
      <Meta name="description" content={meta().description} />
      <Meta
        name="robots"
        content={meta().noindex ? 'noindex,nofollow' : 'index,follow'}
      />

      <Meta property="og:type" content={meta().ogType ?? 'website'} />
      <Meta property="og:title" content={meta().title} />
      <Meta property="og:description" content={meta().description} />
      <Meta property="og:url" content={canonicalUrl()} />
      <Meta property="og:image" content={ogImageUrl()} />
      <Meta property="og:image:alt" content={meta().title} />
      {meta().publishedTime ? (
        <>
          <Meta
            property="article:published_time"
            content={meta().publishedTime!}
          />
          <Meta property="article:author" content="https://khoai.io" />
        </>
      ) : null}

      <Meta name="twitter:card" content="summary_large_image" />
      <Meta name="twitter:title" content={meta().title} />
      <Meta name="twitter:description" content={meta().description} />
      <Meta name="twitter:url" content={canonicalUrl()} />
      <Meta name="twitter:image" content={ogImageUrl()} />
      <Meta name="twitter:image:alt" content={meta().title} />

      <Link rel="canonical" href={canonicalUrl()} />

      {meta().includeAlternates ? (
        <>
          <Link rel="alternate" hreflang="en" href={enAltUrl()} />
          <Link rel="alternate" hreflang="vi" href={viAltUrl()} />
          <Link rel="alternate" hreflang="x-default" href={enAltUrl()} />
        </>
      ) : null}
    </>
  );
}
